import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {fetchSession, requestUploadLink, reportUploaded, uploadFile} from './api.js';

/**
 * External Submission Portal — borrower-facing UI.
 *
 * The portal is intentionally a single-screen, single-purpose app. A borrower lands here from a
 * lender-issued magic link, sees what they owe, drops a file, and leaves. There is no signup, no
 * navigation, no settings. Everything else gets pushed back into the lender's main shell.
 *
 * Three sections drive the layout:
 *  - Open requests: things due (or overdue) — drop a file to fulfill.
 *  - In review: things uploaded but not yet accepted — borrower can see status but not re-upload.
 *  - History: accepted past submissions — read-only, here so the borrower has audit context.
 *
 * Branding (logo, name, primary color) comes from the session payload, so a single deploy can
 * white-label per lender at runtime.
 */

const _readToken = () => {
	const params = new URLSearchParams(window.location.search);
	return params.get('token') || '';
};

const _statusLabel = status =>
	({
		open: 'OPEN',
		pending: 'IN REVIEW',
		accepted: 'ACCEPTED',
		needs_revision: 'NEEDS REVISION',
	})[status] || status.toUpperCase();

const _formatDate = iso => {
	if (!iso) return '';
	try {
		return new Date(iso).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
	} catch {
		return iso;
	}
};

const _isOverdue = (dueDate, status) => {
	if (status !== 'open' && status !== 'needs_revision') return false;
	if (!dueDate) return false;
	return new Date(dueDate) < new Date(new Date().toDateString());
};

const Toast = ({toast, onClose}) => {
	useEffect(() => {
		if (!toast) return;
		const id = setTimeout(onClose, toast.kind === 'error' ? 8000 : 4000);
		return () => clearTimeout(id);
	}, [toast, onClose]);
	if (!toast) return null;
	return <div className={`toast ${toast.kind === 'error' ? 'error' : ''}`}>{toast.message}</div>;
};

const SubmissionCard = ({submission, onUpload, busyId, isReadonly, brandColor}) => {
	const [dragActive, setDragActive] = useState(false);
	const [dataAsOfDate, setDataAsOfDate] = useState('');
	const inputRef = useRef(null);
	const overdue = _isOverdue(submission.dueDate, submission.status);
	const uploading = busyId === submission.id;

	const handleFiles = files => {
		if (!files || !files.length) return;
		onUpload(submission, files[0], dataAsOfDate || null);
	};

	return (
		<div className="card">
			<div className="row">
				<div>
					<div className="label">{submission.label}</div>
					{submission.dueDate ? (
						<div className="muted" style={overdue ? {color: '#b91c1c'} : undefined}>
							Due {_formatDate(submission.dueDate)}
							{overdue ? ' (overdue)' : ''}
						</div>
					) : null}
				</div>
				<span className={`status ${submission.status}`}>{_statusLabel(submission.status)}</span>
			</div>

			{submission.uploadedFileName ? (
				<div className="muted" style={{marginTop: 6}}>
					{submission.uploadedFileName} · uploaded {_formatDate(submission.uploadedAt)}
				</div>
			) : null}

			{submission.reviewerNote ? (
				<div className="muted" style={{marginTop: 6, fontStyle: 'italic'}}>
					Reviewer note: {submission.reviewerNote}
				</div>
			) : null}

		{!isReadonly ? (
			<>
			<div style={{marginTop: 12, marginBottom: 4}}>
				<label style={{display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151'}}>
					Data as-of date <span style={{color: '#ef4444'}}>*</span>
				</label>
				<input
					type="date"
					value={dataAsOfDate}
					onChange={e => setDataAsOfDate(e.target.value)}
					style={{
						width: '100%',
						padding: '6px 10px',
						fontSize: 13,
						border: '1px solid #d1d5db',
						borderRadius: 6,
						outline: 'none',
						boxSizing: 'border-box',
					}}
				/>
				<div style={{fontSize: 11, color: '#6b7280', marginTop: 3}}>
					The effective date of the data in this file (e.g. month-end date).
				</div>
			</div>
			<div
				className={`dropzone ${dragActive ? 'active' : ''} ${!dataAsOfDate ? 'disabled' : ''}`}
				style={{
					...(dragActive ? {borderColor: brandColor, color: brandColor} : undefined),
					...(!dataAsOfDate ? {opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none'} : undefined),
				}}
					style={dragActive ? {borderColor: brandColor, color: brandColor} : undefined}
					onDragEnter={e => {
						e.preventDefault();
						setDragActive(true);
					}}
					onDragOver={e => {
						e.preventDefault();
						setDragActive(true);
					}}
					onDragLeave={() => setDragActive(false)}
					onDrop={e => {
						e.preventDefault();
						setDragActive(false);
						handleFiles(e.dataTransfer.files);
					}}
					onClick={() => inputRef.current?.click()}
					role="button"
					tabIndex={0}
				>
			{uploading ? (
					<>
						<span className="spinner" /> Uploading…
					</>
				) : (
					<>
						<div>Drop a file here or</div>
						<span className="browse" style={{background: brandColor}}>
							Choose file
						</span>
						<input
							ref={inputRef}
							type="file"
							onChange={e => handleFiles(e.target.files)}
						/>
					</>
				)}
			</div>
			</>
		) : null}
		</div>
	);
};

export default function App() {
	const [token] = useState(_readToken);
	const [session, setSession] = useState(null);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(true);
	const [busyId, setBusyId] = useState(null);
	const [toast, setToast] = useState(null);

	const refresh = useCallback(async () => {
		if (!token) {
			setError('Missing portal access link. Please use the link your lender sent you.');
			setLoading(false);
			return;
		}
		try {
			setError(null);
			const next = await fetchSession(token);
			setSession(next);
		} catch (err) {
			setError(err.message || 'Could not load portal session');
		} finally {
			setLoading(false);
		}
	}, [token]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const onUpload = useCallback(
		async (submission, file, dataAsOfDate) => {
			if (!file) return;
			setBusyId(submission.id);
			try {
				const link = await requestUploadLink(token, submission.id);
				const metadata = {
					sourceApplication: 'dataimport',
					displayName: file.name,
				};
				const archiveResult = await uploadFile(link.uploadPath, file, metadata);
				await reportUploaded(token, submission.id, {
					uploadedFileName: file.name,
					artifactId: archiveResult?.artifactId || archiveResult?.data?.artifactId,
					...(dataAsOfDate && {dataAsOfDate}),
				});
				setToast({kind: 'ok', message: `${submission.label}: uploaded ${file.name}`});
				await refresh();
			} catch (err) {
				setToast({kind: 'error', message: `${submission.label}: ${err.message}`});
			} finally {
				setBusyId(null);
			}
		},
		[token, refresh],
	);

	const grouped = useMemo(() => {
		const open = [];
		const review = [];
		const history = [];
		(session?.submissions || []).forEach(s => {
			if (s.status === 'open' || s.status === 'needs_revision') open.push(s);
			else if (s.status === 'pending') review.push(s);
			else history.push(s);
		});
		return {open, review, history};
	}, [session]);

	const brand = session?.primaryColor || '#0B5FFF';
	const cssVars = {'--brand': brand};

	if (loading) {
		return (
			<div className="app" style={cssVars}>
				<div className="container">
					<div className="banner">Loading your portal…</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="app" style={cssVars}>
				<div className="container">
					<div className="banner error">{error}</div>
				</div>
			</div>
		);
	}

	return (
		<div className="app" style={cssVars}>
			<div className="topbar">
				{session.lenderLogoUrl ? <img src={session.lenderLogoUrl} alt={`${session.lenderName} logo`} /> : null}
				<div className="lender">{session.lenderName} · Document Portal</div>
				<div className="borrower">Logged in as {session.borrowerOrgName}</div>
			</div>

			<div className="container">
				<div className="subtitle">Open requests ({grouped.open.length})</div>
				{grouped.open.length === 0 ? (
					<div className="card muted">All caught up — nothing pending right now.</div>
				) : (
					<div className="grid">
						{grouped.open.map(s => (
							<SubmissionCard
								key={s.id}
								submission={s}
								onUpload={onUpload}
								busyId={busyId}
								brandColor={brand}
							/>
						))}
					</div>
				)}

				<div className="subtitle">In review ({grouped.review.length})</div>
				{grouped.review.length === 0 ? (
					<div className="card muted">Nothing waiting on your lender.</div>
				) : (
					<div className="grid">
						{grouped.review.map(s => (
							<SubmissionCard
								key={s.id}
								submission={s}
								onUpload={onUpload}
								busyId={busyId}
								brandColor={brand}
								isReadonly
							/>
						))}
					</div>
				)}

				<div className="subtitle">History ({grouped.history.length})</div>
				{grouped.history.length === 0 ? (
					<div className="card muted">No accepted submissions yet.</div>
				) : (
					<div className="grid">
						{grouped.history.map(s => (
							<SubmissionCard
								key={s.id}
								submission={s}
								onUpload={onUpload}
								busyId={busyId}
								brandColor={brand}
								isReadonly
							/>
						))}
					</div>
				)}
			</div>

			<div className="footer">
				Need help? Contact {session.contactEmail || 'your lender'}.
			</div>

			<Toast toast={toast} onClose={() => setToast(null)} />
		</div>
	);
}
